import styled, { css } from '@lokalise/ui/styled';
import { outline } from '@lokalise/ui/styles';
import { color, modifier, spacing, typography } from '@lokalise/ui/theme';

import { focusOutlineVisibilityClassName } from '../FocusOutlineVisibilityProvider';
import CheckboxChecked from './icons/CheckboxChecked';
import CheckboxCheckedFilled from './icons/CheckboxCheckedFilled';
import CheckboxEmpty from './icons/CheckboxEmpty';

export const Container = styled.div`
    position: relative;
    display: grid;
    grid-auto-flow: row;
    gap: ${spacing(1)};
    width: 100%;
`;

export const Label = styled.label`
    display: grid;
    grid-template-columns: min-content;
    grid-auto-flow: column;
    gap: ${spacing(1)};

    /* Required because of global styles applied by bootstrap */
    margin: 0;
`;

interface CheckboxIconProps {
    checked?: boolean;
}

const iconSize = '20px';

export const DisabledCheckbox = ({ checked }: CheckboxIconProps) =>
    checked ? (
        <CheckboxCheckedFilled color="content.disabled" size={iconSize} />
    ) : (
        <CheckboxEmpty color="content.disabled" secondaryColor="surface.secondary" size={iconSize} />
    );

// eslint-disable-next-line react/no-multi-comp
export const ErrorCheckbox = ({ checked }: CheckboxIconProps) =>
    checked ? (
        <CheckboxChecked color="red.600" secondaryColor="surface.red50" size={iconSize} />
    ) : (
        <CheckboxEmpty color="red.600" secondaryColor="surface.red50" size={iconSize} />
    );

export const CheckboxDefault = styled.svg.attrs<CheckboxIconProps>(({ checked }) => ({
    as: checked ? CheckboxCheckedFilled : CheckboxEmpty,
}))<CheckboxIconProps>`
    stroke: ${color('content.secondary')};
    font-size: ${iconSize};

    ${modifier(
        'checked',
        css`
            color: ${color('primary.500')};
            stroke: ${color('primary.500')};
            path {
                stroke: none;
            }
        `,
    )};

    ${Container}:hover &,
    [data-dev-state='hover'] & {
        color: ${color('interactive.primary.hover')};
        stroke: ${color('interactive.primary.hover')};
    }
    ${Container}:active &,
    [data-dev-state='active'] & {
        color: ${color('interactive.primary.pressed')};
        stroke: ${color('interactive.primary.pressed')};
    }
`;

export const Input = styled.input`
    && {
        // increases the specificity of the selector to solve the problem with conflicting styles from Bootstrap.
        position: absolute !important; // !important is necessary because it conflicts with bootstrap styles.
        opacity: 0;
        outline: none;
    }

    /*
    * This is a solution to have a full keyboard support for Checkbox.
    * "focusOutlineVisibilityClassName" is set on "body" tag and it indicates whether
    * user is using keyboard to navigate a page. Focus outline is only visible when user is using 
    * keyboard. This behavior matches native checkbox behavior.
    */
    .${focusOutlineVisibilityClassName} &:focus + svg {
        ${outline}
    }
`;

export const Text = styled.span`
    ${typography('description')};
    color: ${color('content.primary')};
    cursor: default;
`;

export const MultiCheckboxContainer = styled.div`
    width: 100%;
`;

export const MultiCheckbox = styled.div`
    display: grid;
    gap: ${spacing(3)};
`;

export const MultiCheckboxErrorContainer = styled.div`
    padding-top: ${spacing(3)};
    display: grid;
    gap: ${spacing(1)};
`;
